import {addContent, addContentNewLine, addOutputLine, isContinueProcessing} from "./util";
import {collectDataCategory, processDataCategory, getDateTime, processDataCategoryStats, processSettings} from "./process-spec";

/** Data category key for closed-loop aggressiveness level records. */
const modeAggressiveness = "aggressiveness";
/** Data category key for attempting-mode status records. */
const modeAttempting = "attempting";
/** Data category key for auto-mode statistics records. */
const modeStats = "stats";
/** Data category key for body weight records. */
const modeWeight = "weight";
/** Data category key for closed-loop (CL) mode status records. */
const modeCl_mode = "cl_mode";
// const modeExercise = "exercise";  // exercise category not currently used
/** Data category key for insulin reservoir level records. */
const modeReservoir = "res";
/** Data category key for pump priming event records. */
const modePriming = "priming";
/** Data category key for pump alarm records. */
const modePump_alarm = "pump_alarm";
/** Data category key for insulin reservoir refill event records. */
const modeRefill = "refill";
/** Data category key for algorithmically deduced refill event records. */
const modeRefillDeduced = "refillDeduced";
/** Data category key for CGM sensor warm-up / insertion event records. */
const modeWarm_up = "warm_up";
/** Data category key for CGM sensor stopped event records. */
const modeSensor_stopped = "sensor_stopped";
/** Data category key for audio alert event records. */
const modeAlert_audio = "alert_audio";
/** Data category key for total daily dose (TDD) records. */
const modeTdd = "tdd";
/** Data category key for algorithm-calculated TDD records. */
const modeTdd_algorithm = "tdd_algorithm";
/** Data category key for vibrate alert event records. */
const modeAlert_vibrate = "alert_vibrate";
/** Data category key for acknowledged alert event records. */
const modeAlert_acknowledged = "alert_ack";
/** Data category key for displayed alert event records. */
const modeAlert_displayed = "alert_displayed";

/**
 * Writes a single aggressiveness record to the output.
 * Output format: timestamp \t\t level(%) \t duration(minutes)
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`, `level.val` (%), and `duration.val` (seconds).
 */
function writeAggressiveness(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + obj.level.val + '\t' + (obj.duration.val / 60));
}

/**
 * Writes a single attempting-mode status record to the output.
 * Output format: timestamp \t\t mode-string
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `cl_mode` (numeric status code).
 */
function writeAttempting(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + getAttemptingString(obj.cl_mode));
}

/**
 * Writes a single auto-mode statistics record to the output.
 * Output format: timestamp \t\t cl_mode \t\t fcl_mode
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`, `cl` (CL mode value), and optional `fcl` (FCL mode value).
 */
function writeAutoModeStats(self, obj) {
    var fclString = "off";
    if (obj.fcl) {
        fclString = obj.fcl;
    }
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + obj.cl + '\t\t' + fclString);
}

/**
 * Writes a personal glucose target profile record to the output, converting to mmol/L if needed.
 * Output format: timestamp \t\t space-separated profile values (mmol/L, 1 d.p.)
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `personalTargetProfile` with `values` array and `units` string.
 * @returns {number[]} The raw profile values array (before unit conversion).
 */
function writeTargetGlucose(self, obj) {
    let str = "";
    let profile = obj.personalTargetProfile.values;
    let units = obj.personalTargetProfile.units;
    let convert = 1.0;
    const ten = 10.0;
    if (units === "mg/dL") {
        convert = 0.05551;
    }
    let val;
    for (val of profile) {
        str = str + " " + Math.round(val * convert * ten)/ten;
    }
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + str);
    return profile;
}

/**
 * Writes a correction factor (CF) profile record to the output if present.
 * Output format: timestamp \t\t space-separated CF values + units
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and optional `cfProfile` with `values` array and `units` string.
 * @returns {number[]|string} The CF profile values array, or an empty string if no CF profile present.
 */
function writeCfProfile(self, obj) {
    let str = "";
    if (obj.cfProfile) {
        let profile = obj.cfProfile.values;
        let units = obj.cfProfile.units;
        let val;
        for (val of profile) {
            str = str + " " + val;
        }
        addOutputLine(self, getDateTime(obj.date) + '\t\t' + str + " " + units);
        return profile;
    }
    return "";
}

/**
 * Writes an insulin-to-carb ratio (ICR) profile record to the output if a CF profile is also present.
 * Output format: timestamp \t\t space-separated ICR values + units
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`, `cfProfile` (used as existence check),
 *                       and `icrProfile` with `values` array and `units` string.
 * @returns {number[]|string} The ICR profile values array, or an empty string if no CF profile present.
 */
function writeIcrProfile(self, obj) {
    let str = "";
    if (obj.cfProfile) {
        let profile = obj.icrProfile.values;
        let units = obj.icrProfile.units;
        let val;
        for (val of profile) {
            str = str + " " + val;
        }
        addOutputLine(self, getDateTime(obj.date) + '\t\t' + str + " " + units);
        return profile;
    }
    return "";
}

/**
 * Writes a preprogrammed basal insulin profile record to the output if a CF profile is also present.
 * Output format: timestamp \t\t space-separated basal values + units
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`, `cfProfile` (used as existence check),
 *                       and `basalProfile` with `values` array and `units` string.
 * @returns {number[]|string} The basal profile values array, or an empty string if no CF profile present.
 */
function writeBasalProfile(self, obj) {
    let str = "";
    if (obj.cfProfile) {
        let profile = obj.basalProfile.values;
        let units = obj.basalProfile.units;
        let val;
        for (val of profile) {
            str = str + " " + val;
        }
        addOutputLine(self, getDateTime(obj.date) + '\t\t' + str + " " + units);
        return profile;
    }
    return "";
}


/**
 * Returns true if the personal glucose target profile in `obj` differs from `previousLine`.
 * Used by {@link processSettings} to suppress duplicate consecutive settings records.
 * @param {object} self - Output state object (unused here, present for interface consistency).
 * @param {object} obj - Data record that may contain a `personalTargetProfile.values` array.
 * @param {number[]} previousLine - The previously written profile values array to compare against.
 * @returns {boolean} True if the profile has changed, false if unchanged or absent.
 */
function checkIfDifferentFromPreviousGlucoseTarget(self, obj, previousLine) {
    var result = false;
    if (obj.personalTargetProfile) {
        result = JSON.stringify(obj.personalTargetProfile.values)
            !== JSON.stringify(previousLine);
    }
    return result;
}

/**
 * Returns true if the preprogrammed basal insulin profile in `obj` differs from `previousLine`.
 * Used by {@link processSettings} to suppress duplicate consecutive settings records.
 * @param {object} self - Output state object (unused here, present for interface consistency).
 * @param {object} obj - Data record that may contain a `basalProfile.values` array.
 * @param {number[]} previousLine - The previously written profile values array to compare against.
 * @returns {boolean} True if the profile has changed, false if unchanged or absent.
 */
function checkIfDifferentFromPreviousBasalProfile(self, obj, previousLine) {
    var result = false;
    if (obj.basalProfile) {
        result = JSON.stringify(obj.basalProfile.values)
            !== JSON.stringify(previousLine);
    }
    return result;
}

/**
 * Returns true if the insulin-to-carb ratio (ICR) profile in `obj` differs from `previousLine`.
 * Used by {@link processSettings} to suppress duplicate consecutive settings records.
 * @param {object} self - Output state object (unused here, present for interface consistency).
 * @param {object} obj - Data record that may contain an `icrProfile.values` array.
 * @param {number[]} previousLine - The previously written profile values array to compare against.
 * @returns {boolean} True if the profile has changed, false if unchanged or absent.
 */
function checkIfDifferentFromPreviousIcrProfile(self, obj, previousLine) {
    var result = false;
    if (obj.icrProfile) {
        result = JSON.stringify(obj.icrProfile.values)
            !== JSON.stringify(previousLine);
    }
    return result;
}

/**
 * Returns true if the correction factor (CF) profile in `obj` differs from `previousLine`.
 * Used by {@link processSettings} to suppress duplicate consecutive settings records.
 * @param {object} self - Output state object (unused here, present for interface consistency).
 * @param {object} obj - Data record that may contain a `cfProfile.values` array.
 * @param {number[]} previousLine - The previously written profile values array to compare against.
 * @returns {boolean} True if the profile has changed, false if unchanged or absent.
 */
function checkIfDifferentFromPreviousCfProfile(self, obj, previousLine) {
    var result = false;
    if (obj.cfProfile) {
        result = JSON.stringify(obj.cfProfile.values)
            !== JSON.stringify(previousLine);
    }
    return result;
}

/**
 * Writes a body weight record to the output.
 * Output format: timestamp \t\t weight(kg)
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `weight.val` (kg).
 */
function writeBodyWeight(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + obj.weight.val);
}

/**
 * Writes a closed-loop mode status record to the output.
 * Output format: timestamp \t\t mode-string
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `cl_mode` (numeric mode code).
 */
function writeClMode(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + getAutoModeString(obj.cl_mode));
}

/**
 * Writes an insulin reservoir level record to the output.
 * Output format: timestamp \t\t amount(U)
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `amt.val` (units of insulin remaining).
 */
function writeReservoir(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + obj.amt.val)
}

/**
 * Writes a pump priming event record to the output (timestamp only).
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`.
 */
function writePriming(self, obj) {
    addOutputLine(self, getDateTime(obj.date))
}

/**
 * Writes a timestamped event record to the output (timestamp only, no additional data).
 * Used for events where only the occurrence time is relevant (e.g. refill, sensor start/stop).
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`.
 */
function writeDateStamp(self, obj) {
    addOutputLine(self, getDateTime(obj.date))
}

/**
 * Lookup map from message ID string to a human-readable description.
 * Covers all component libraries used by CamAPS FX:
 *  - FXMCGM (CGM glucose alerts), FXM (app/system messages), FXHLPM (in-app help messages)
 *  - G7X / DG7HSC (Dexcom G7 library)
 *  - DCI (Dana I pump library)
 *  - DXC (Dexcom G5/G6 library)
 *  - LBR (Libre 3 library)
 *  - YPS (Ypsomed 15x pump library)
 *  - FLW (Cloud Follow CGM library)
 * Unknown IDs pass through unchanged via {@link decodeMessageID}.
 * @type {Object.<string, string>}
 */
// Lookup map from message ID string to human-readable description.
// Covers all libraries: FX app (FXM/FXMCGM), Dexcom G7 (G7X/DG7HSC),
// Dana I pump (DCI), Dexcom G5/G6 (DXC), Libre 3 (LBR),
// Ypsomed pump (YPS), and Cloud Follow CGM (FLW).
// Unknown IDs pass through unchanged.
const MESSAGE_ID_MAP = {

    // ── FXMCGM: CGM glucose alerts ───────────────────────────────────────────
    // Source: UserMessageCodes.ERROR_MESSAGES_CGM
    "FXMCGM001": "Urgent Low Glucose",
    "FXMCGM002": "Low Glucose",
    "FXMCGM003": "High Glucose",
    "FXMCGM004": "Rising Glucose",
    "FXMCGM005": "Falling Glucose",
    "FXMCGM006": "Signal Loss",
    "FXMCGM007": "Urgent Low Glucose Soon",

    // ── FXM: App / system messages ───────────────────────────────────────────
    // Source: UserMessageCodes.ERROR_MESSAGES
    // General system errors
    "FXM001": "Unknown Error",
    "FXM002": "System Error",
    "FXM003": "Shutting Down",
    "FXM004": "Alerts Reset",
    "FXM005": "PDF Reader Error",
    // Android permissions
    "FXM006": "Location Permission Missing",
    "FXM007": "External Storage Permission Missing",
    "FXM008": "SMS Permission Missing",
    "FXM009": "Battery Optimisation Not Ignored",
    // Bluetooth / Dana R pump setup
    "FXM010": "Pair Dana R Pump",
    "FXM011": "Turn On Bluetooth",
    "FXM012": "No Bluetooth Adapter",
    "FXM013": "Dana R Not Found",
    "FXM014": "Dana R Serial Number Mismatch",
    "FXM015": "Bluetooth Off",
    "FXM016": "Dana Pump No Password",
    "FXM017": "Wrong Dana Password",
    "FXM018": "Wrong Dana Password (3 Times)",
    "FXM019": "Dana Password Changed",
    // Diasend upload account / pump time sync
    "FXM020": "Wrong Diasend Password",
    "FXM021": "Diasend Password OK",
    "FXM022": "Pump Time Sync Failed",
    "FXM023": "Pump Time Synced",
    // G6 sensor code / second Diasend account
    "FXM024": "G6 Sensor Code Invalid",
    "FXM025": "No G6 Sensor Code",
    "FXM026": "Wrong Diasend 2nd Password",
    "FXM027": "Diasend 2nd Password OK",
    // Bolus delivery warnings
    "FXM028": "Extended Bolus Under-delivery",
    "FXM029": "Daily TDD Exceeded",
    "FXM030": "Active Insulin Warning",
    "FXM031": "Unable To Start Bolus",
    "FXM032": "Unable To Start Step Bolus",
    "FXM033": "Unable To Start Extended Bolus",
    "FXM034": "Step Bolus Amount Unclear",
    "FXM035": "Step Bolus Delivered",
    // Bolus wizard (BW) / home screen status
    "FXM036": "Home Screen",
    "FXM037": "BW Unable To Connect To Pump",
    "FXM038": "BW Delivery Suspended",
    "FXM039": "BW TDD Exceeded",
    "FXM040": "Bolus Wizard Breakdown",
    "FXM041": "BW Device Not Secured",
    "FXM042": "BW Confirm Bolus Amount",
    // General / miscellaneous
    "FXM043": "Weight Changed",
    "FXM044": "Check Time Is Correct",
    "FXM045": "Unable To Stop Bolus",
    // Login / signup / AWS authentication
    "FXM046": "Login Failed",
    "FXM047": "Login Succeeded",
    "FXM048": "Signup Succeeded",
    "FXM049": "Login Unknown Error",
    "FXM050": "Unable To Connect To Server",
    "FXM051": "Signup Unknown Error",
    "FXM052": "Signup Incorrect Password",
    "FXM053": "Signup User Already Exists",
    "FXM054": "Unknown Login Error",
    "FXM055": "Password Reset",
    "FXM056": "Wrong Verification Code",
    "FXM057": "Too Many Verification Attempts",
    // Diasend / SMS follower linking
    "FXM058": "Diasend Account Linked",
    "FXM059": "Diasend Wrong Credentials",
    "FXM060": "Diasend Server Connection Failed",
    "FXM061": "Test Alert",
    "FXM062": "Follower Account Linked",
    "FXM063": "Follower Wrong Code",
    "FXM064": "Verification Code Sent",
    // Pump serial number validation
    "FXM065": "Checking Pump SN - No Internet",
    "FXM066": "Pump SN Not Allowed",
    "FXM067": "Invalid Login (Pump SN Not Checked)",
    "FXM068": "Pump Expired",
    "FXM069": "Verification Code Not Sent",
    // Dana RS/I pump linking / reservoir / AWS S3
    "FXM070": "Dana RS Not Paired",
    "FXM071": "High Bolus Increment",
    "FXM072": "Dana R Linked",
    "FXM073": "Dana RS Linked",
    "FXM074": "Low Reservoir",
    "FXM075": "Very Low Reservoir",
    "FXM076": "Communication Restarted",
    "FXM077": "AWS S3 Upload Success",
    "FXM078": "AWS S3 Upload Failed",
    "FXM079": "User Manual Download Failed",
    // Clinic / trainer / connectivity
    "FXM080": "User Disabled",
    "FXM081": "Clinic ID Not Found",
    "FXM082": "Clinic ID Linked",
    "FXM083": "No Internet",
    "FXM084": "App Settings Restored",
    "FXM085": "Trainer ID Not Found",
    "FXM086": "Trainer ID Found",
    "FXM087": "Step Bolus Under-delivery",
    "FXM088": "Version Not Supported",
    // Algorithm / pairing / storage / alerts / refill
    "FXM089": "Wrong Decreasing Ratio",
    "FXM090": "No Storage Space",
    "FXM091": "Wrong Pairing Keys",
    "FXM092": "Pairing Request",
    "FXM093": "Share Settings Not Restored",
    "FXM094": "Too Many Login Attempts",
    "FXM095": "Refill Alert",
    // App versioning and safety notices (FXM097-FXM106 are version-specific notices)
    "FXM096": "App Version Expired",
    "FXM097": "App Version Notice",
    "FXM098": "App Version Notice",
    "FXM099": "App Version Notice",
    "FXM100": "App Version Notice",
    "FXM101": "App Version Notice",
    "FXM102": "App Version Notice",
    "FXM103": "App Version Notice",
    "FXM104": "App Version Notice",
    "FXM105": "App Version Notice",
    "FXM106": "App Version Notice",
    "FXM107": "Safety Notice Review Required",
    "FXM108": "Safety Notice Download Failed",
    // Clarity (Dexcom cloud) / Dexcom follower / Dana I pump
    "FXM109": "Clarity Account Linked",
    "FXM110": "Unable To Login To Clarity",
    "FXM111": "Easy Menu",
    "FXM112": "Basal Units U/Day",
    "FXM113": "Dana I Not Paired",
    "FXM114": "Dana I Linked",
    "FXM115": "Last Bolus Delivered",
    "FXM116": "BW Unable To Connect To Pump (CHD)",
    "FXM117": "No Clarity",
    "FXM118": "Share Internet Error",
    "FXM119": "Dexcom Follower Credentials Error",
    "FXM120": "Dexcom Follower Server Error",
    "FXM121": "Dexcom Follower Invited",
    "FXM122": "Dexcom Follower Unlinked",
    "FXM123": "Dexcom Manual Pairing",
    // Consent / pump suspension / algorithm
    "FXM124": "Consent Not Provided",
    "FXM125": "Pump Suspended",
    "FXM126": "Reservoir Empty",
    "FXM127": "Algorithm Settings Restored",
    "FXM128": "Dexcom Follow Consent Not Granted",
    "FXM129": "Dexcom Clarity Consent Not Granted",
    // Ypsomed pump
    "FXM130": "Ypso Pump Linked",
    "FXM131": "Ypso Pump Not Paired",
    "FXM132": "Wrong Insulin Action Duration",
    "FXM133": "Ypso Pump Setup BW",
    "FXM134": "Glucose Too Low For Bolus Calculator",
    "FXM135": "Ypso Pump BW Setup Completed",
    "FXM136": "TDD Weight Info",
    "FXM137": "TDD Weight Done",
    // Demo mode
    "FXM138": "Demo Pump Linked",
    "FXM139": "Demo Pump Selected",
    "FXM140": "Demo Pump Warning",
    "FXM141": "Demo CGM Warning",
    // Ypsomed BLE sync
    "FXM142": "Ypso Sync In Progress",
    "FXM143": "Ypso Sync Completed",
    "FXM144": "Ypso Sync Failed",
    "FXM145": "Ypso No Scan",
    // Alert settings / NFC / Libre sensor
    "FXM146": "Follower Code Not Sent",
    "FXM147": "Check Alert Settings",
    "FXM148": "Check BW Settings",
    "FXM149": "Reset Alerts",
    "FXM150": "Turn On NFC",
    "FXM151": "Libre Error",
    "FXM152": "Libre Scan Error",
    "FXM153": "Libre Warning (Connect Pump)",
    "FXM154": "Libre Final Warning (Connect Pump)",
    "FXM155": "Libre No Encryption Key",
    "FXM156": "Libre Integrity Fail",
    "FXM157": "No NFC",
    "FXM158": "Zero Basal",
    "FXM159": "Review Alert Sounds",
    // Cloud follow pump / companion / SMS followers
    "FXM160": "Cloud Follow Account Linked",
    "FXM161": "Cloud Follow Account Update Failed",
    "FXM162": "Low Battery",
    "FXM163": "Follower Pump Selected",
    "FXM164": "Cloud Follow Not Allowed",
    "FXM165": "Cloud Follow Pump Linked",
    "FXM166": "PDF Report Download Failed",
    "FXM167": "Text Report Import Failed",
    "FXM168": "Feature Not Available (Pump)",
    "FXM169": "Feature Not Available (CGM)",
    "FXM170": "Too Many SMS Followers",
    "FXM171": "Too Many SMS Followers (Notice)",
    "FXM172": "SMS Follower Not Allowed",
    "FXM173": "Companion Not Allowed",
    "FXM174": "Follower Last Data",
    "FXM175": "Companion Upload Error",
    "FXM176": "Companion Download Error",
    // Sensor expiry (cloud follow sensor)
    "FXM177": "Sensor Expires In 6 Hours",
    "FXM178": "Sensor Expires In 2 Hours",
    "FXM179": "Sensor Expires In 30 Min",
    "FXM180": "Sensor Expired",
    // Integrity / bolus / carbs / audio / password / FCL
    "FXM181": "Step Bolus Alert",
    "FXM182": "Integrity Check Success",
    "FXM183": "Integrity Check Failure",
    "FXM184": "Maximum Bolus Reached",
    "FXM185": "Carb Unit Updated",
    "FXM186": "Dexcom Transmitter Unpaired",
    "FXM187": "Sound Test Alert",
    "FXM188": "Vibrate Test Alert",
    "FXM189": "Password Reset (User)",
    "FXM190": "MyLife Cloud Password Wrong",
    "FXM191": "MyLife Cloud Password OK",
    "FXM192": "Preprogrammed Basal Changed",
    "FXM193": "Bolus Calculator Unavailable",
    "FXM194": "Liberty FCL Info",            // Fully Closed Loop info message
    "FXM195": "Liberty FCL No ACL",          // FCL without automated closed-loop warning

    // ── FXM301-307: Glooko XT integration ────────────────────────────────────
    "FXM301": "XT Email Not Registered",
    "FXM302": "XT Wrong Code",
    "FXM303": "XT No Server",
    "FXM304": "XT Unknown Error",
    "FXM305": "XT Linked OK",
    "FXM306": "XT Link Failure",
    "FXM307": "XT Expiring Soon",

    // ── FXM350-354: Dexcom G7 follower (via FX app) ───────────────────────────
    "FXM350": "G7 Login Failed",
    "FXM351": "G7 Sensor Expires In 24 Hours",
    "FXM352": "G7 Sensor Expires In 2 Hours",
    "FXM353": "G7 Sensor Grace Period",
    "FXM354": "G7 Sensor Expired",

    // ── FXM400-402: Training ──────────────────────────────────────────────────
    "FXM400": "Training",
    "FXM401": "Training Needed",
    "FXM402": "Training Extra",

    // ── FXHLPM: In-app help / context messages ────────────────────────────────
    // Source: UserMessageCodes.HELP_MESSAGES
    // These are contextual help pop-ups shown within the CamAPS FX app screens.
    // CGM selection
    "FXHLPM001": "Help: CGM Selection Screen",
    "FXHLPM002": "Help: Sample Help",
    // Pump selection
    "FXHLPM003": "Help: Pump Selection Screen",
    // Dexcom G5/G6 setup
    "FXHLPM004": "Help: Dexcom Serial Number Entry",
    "FXHLPM005": "Help: BG Calibration Entry",
    // Attempting mode reasons (explain why closed loop is not running)
    "FXHLPM006": "Help: Attempting Reason - Unknown",
    "FXHLPM007": "Help: Attempting Reason - Starting",
    "FXHLPM008": "Help: Attempting Reason - Stopping",
    "FXHLPM009": "Help: Attempting Reason - No CGM",
    "FXHLPM010": "Help: Attempting Reason - No Pump Connectivity",
    "FXHLPM011": "Help: Attempting Reason - Pump Delivery Suspended",
    "FXHLPM012": "Help: Attempting Reason - Extended Bolus Disabled",
    "FXHLPM013": "Help: Attempting Reason - TDD Exceeded",
    "FXHLPM014": "Help: Attempting Reason - Bluetooth Off",
    "FXHLPM015": "Help: Block Icon",
    // Device selection info
    "FXHLPM016": "Help: Dexcom G5/G6 Selected",
    "FXHLPM017": "Help: Dana R Selected",
    "FXHLPM018": "Help: First Dexcom Calibration",
    "FXHLPM019": "Help: Dana Pump Password",
    "FXHLPM020": "Help: Dexcom Sensor Code Entry",
    // Algorithm initialisation
    "FXHLPM021": "Help: Body Weight Entry",
    "FXHLPM022": "Help: TDD Entry",
    // Main screens
    "FXHLPM023": "Help: Home Screen",
    "FXHLPM024": "Help: Personal Target Screen",
    // Boost / ease-off
    "FXHLPM025": "Help: Boost",
    "FXHLPM026": "Help: Ease Off",
    // Pairing / connectivity / session
    "FXHLPM027": "Help: Dana R Pairing Info",
    "FXHLPM028": "Help: Signal Loss Info",
    "FXHLPM029": "Help: Stop Session",
    // Meal entry
    "FXHLPM030": "Help: Enter Snack",
    // Stats screens
    "FXHLPM031": "Help: Low Glucose Target Stats",
    "FXHLPM032": "Help: High Glucose Target Stats",
    // Personal glucose target
    "FXHLPM033": "Help: Personal Glucose Target (mmol/L)",
    "FXHLPM034": "Help: Personal Glucose Target (mg/dL)",
    // Alerts
    "FXHLPM035": "Help: Alerts Turned Off",
    // Bolus wizard (BW)
    "FXHLPM036": "Help: BW Connection Screen",
    "FXHLPM037": "Help: BW No Glucose (Closed Loop)",
    "FXHLPM038": "Help: Bolus Step 0.05U",
    // Login / account screens
    "FXHLPM039": "Help: Login Screen",
    "FXHLPM040": "Help: Signup Screen",
    "FXHLPM041": "Help: Set Up New Password Screen",
    "FXHLPM042": "Help: Verify Code Screen",
    // Upload status
    "FXHLPM043": "Help: Diasend Upload Stopped",
    "FXHLPM044": "Help: Diasend Upload Restarted",
    "FXHLPM045": "Help: Follower Upload Stopped",
    "FXHLPM046": "Help: Follower Upload Restarted",
    "FXHLPM047": "Help: Stats Swipe Hint",
    // Dana RS setup
    "FXHLPM048": "Help: Dana RS Selection Screen",
    "FXHLPM049": "Help: Dana RS Selected",
    "FXHLPM050": "Help: Dana RS Selection Screen (Pairing)",
    "FXHLPM051": "Help: Decreasing Ratio",
    // Boost / ease-off (auto mode inactive)
    "FXHLPM052": "Help: Boost (No Auto Mode)",
    "FXHLPM053": "Help: Ease Off (No Auto Mode)",
    // Diagnostics / trainer
    "FXHLPM054": "Help: Submit Logs",
    "FXHLPM055": "Help: Trainer ID Entry",
    // Alert schedules
    "FXHLPM056": "Help: Schedule Name",
    "FXHLPM057": "Help: Schedule Start Time",
    "FXHLPM058": "Help: Schedule End Time",
    "FXHLPM059": "Help: Schedule - Select At Least One Day",
    "FXHLPM060": "Help: Schedule Created",
    // Clarity (Dexcom cloud) upload
    "FXHLPM061": "Help: Clarity Upload Stopped",
    "FXHLPM062": "Help: Clarity Upload Restarted",
    // Dana I setup
    "FXHLPM063": "Help: Dana I Selection Screen",
    "FXHLPM064": "Help: Dana I Selected",
    "FXHLPM065": "Help: Dana I Selection Screen (Pairing)",
    // Dexcom follower upload
    "FXHLPM066": "Help: Dexcom Follower Stopped",
    "FXHLPM067": "Help: Dexcom Follower Upload Restarted",
    // Consent screens
    "FXHLPM068": "Help: Medical Data Processing Consent",
    "FXHLPM069": "Help: Data Sharing Consent",
    "FXHLPM070": "Help: Marketing Consent",
    // Meal entry types
    "FXHLPM071": "Help: Slow Carbs Meal",
    // Ypsomed pump setup
    "FXHLPM072": "Help: Ypsomed Pump Selected",
    "FXHLPM073": "Help: Ypso Pump Selection Screen (Pairing)",
    "FXHLPM074": "Help: Ypso Pump Selection Screen",
    "FXHLPM075": "Help: Ypso Pump - Turn On Bluetooth Before Pairing",
    // Bolus wizard settings help
    "FXHLPM076": "Help: Bolus Wizard Glucose Target",
    "FXHLPM077": "Help: Bolus Wizard ICR",
    "FXHLPM078": "Help: Bolus Wizard CF",
    // Demo mode
    "FXHLPM079": "Help: Demo Pump Selection Screen",
    "FXHLPM080": "Help: Demo CGM Selected",
    "FXHLPM081": "Help: Demo Pump Selected",
    // Meal types
    "FXHLPM082": "Help: Hypo Treatment Meal",
    "FXHLPM083": "Help: Snack Meal",
    // Libre 3 setup
    "FXHLPM084": "Help: Libre 3 Selected",
    "FXHLPM085": "Help: Apply Libre Sensor",
    "FXHLPM086": "Help: Start Libre Sensor (Scan)",
    "FXHLPM087": "Help: Apply Libre Sensor (Pop-up)",
    "FXHLPM088": "Help: Start Libre Sensor Pop-up",
    "FXHLPM089": "Help: No Calibration Screen",
    "FXHLPM090": "Help: No Calibration Warning (Pop-up)",
    // Cloud follower upload
    "FXHLPM091": "Help: Cloud Follower Stopped",
    "FXHLPM092": "Help: Cloud Follower Upload Restarted",
    "FXHLPM093": "Help: Cloud Follow Pump Selected",
    // Miscellaneous
    "FXHLPM094": "Help: Install File Viewer",
    "FXHLPM095": "Help: Data Not Shared (Virtual Pump)",
    // Meal / bolus calculator usage
    "FXHLPM096": "Help: Meal Entry With Bolus Calculator",
    "FXHLPM097": "Help: Change Meal Size For Bolus Calculator",
    "FXHLPM098": "Help: Bolus Calculator Usage",
    "FXHLPM099": "Help: Using Add Meal",
    "FXHLPM100": "Help: Using Dexcom G6",
    "FXHLPM101": "Help: Overwrite Bolus Amount",
    // Alert states
    "FXHLPM102": "Help: Alerts Off (Including Urgent Low)",
    "FXHLPM103": "Help: Alerts Off (Urgent Low On Vibrate)",
    // Login / session management
    "FXHLPM104": "Help: Logout Warning",
    "FXHLPM105": "Help: Logout Due To Inactivity",
    // Verification codes
    "FXHLPM106": "Help: Verification Code Sent",
    "FXHLPM107": "Help: Verification Code Not Sent",
    "FXHLPM108": "Help: Verification Code Expired",
    // Password reset
    "FXHLPM109": "Help: Set Up New Password (User Reset)",
    // Fully Closed Loop (FCL) information
    "FXHLPM110": "Help: FCL Start Info",
    "FXHLPM111": "Help: FCL End Info",
    "FXHLPM112": "Help: FCL After Start Info",
    "FXHLPM113": "Help: FCL Before Training Info",
    "FXHLPM114": "Help: FCL Open Loop Warning",
    // Dexcom G7 setup
    "FXHLPM115": "Help: Dexcom G7 Selected",
    "FXHLPM116": "Help: Using Dexcom G7",
    "FXHLPM117": "Help: G7 Calibration Warning",
    "FXHLPM118": "Help: Dexcom G7 Pairing Code Entry",
    "FXHLPM119": "Help: Cannot Change From G7 (Logout Required)",
    "FXHLPM120": "Help: Stop Session (G7)",

    // ── G7X: Dexcom G7 library ────────────────────────────────────────────────
    // Source: HandleErrorMessage (dexcomg7) / G7EventInterface
    // Internal errors / integrity
    "G7X001": "Unknown Error",
    "G7X002": "CRC Check Error",
    "G7X003": "CRC Calculation Error",
    "G7X004": "Transmitter Failed",
    // Sensor lifecycle
    "G7X005": "Sensor Expires In 24 Hours",
    "G7X006": "Sensor Expires In 2 Hours",
    "G7X007": "Sensor Expired (Grace Period Remaining)",
    "G7X008": "Sensor Expired (No Grace Period)",
    // Glucose alerts
    "G7X009": "Urgent Low Glucose",
    "G7X010": "Low Glucose",
    "G7X011": "High Glucose",
    "G7X012": "Falling Glucose",
    "G7X013": "Rising Glucose",
    "G7X014": "Signal Loss",
    // Sensor status / failure
    "G7X015": "Sensor Failed",
    "G7X016": "Session Failed - Out Of Range",
    "G7X017": "Temporary Reading Problem",
    "G7X018": "Sensor Stopped",
    "G7X019": "Sensor Failed (SIV)",
    // Pairing
    "G7X020": "Pairing Failed",
    "G7X021": "Pairing In Progress",
    "G7X022": "Transmitter Paired",
    // Database errors
    "G7X023": "Database Creation Failed",
    "G7X024": "Database Delete Failed",
    "G7X025": "Database Integrity Failed",
    "G7X026": "Database Context Null",
    "G7X027": "Database Open Exception",
    "G7X028": "Database Get Exception",
    "G7X029": "Database Get Exception",
    "G7X030": "Database Insert Failed",
    "G7X031": "Database Insert Exception",
    "G7X032": "Database Delete Exception",
    "G7X033": "Database Delete Exception",
    "G7X034": "Message Acknowledgement Exception",
    "G7X035": "Restart App",
    "G7X036": "Urgent Low Glucose Soon",
    "G7X037": "Database Delete Exception",
    // System / hardware
    "G7X038": "No Factory Time",
    "G7X039": "No Disk Space",
    "G7X040": "Root Certificate Missing",
    "G7X041": "Dexcom Certificate Missing",
    // Sensor warm-up / calibration
    "G7X042": "Sensor Warm Up",
    "G7X043": "Calibration Not Used",

    // ── DG7HSC: G7 home screen messages ──────────────────────────────────────
    // Source: G7EventInterface constant strings
    "DG7HSC003": "Start Sensor",
    "DG7HSC007": "Signal Loss",

    // ── DCI: Dana I pump library ──────────────────────────────────────────────
    // Source: danadiabecare_i HandleErrorMessage
    // Internal data errors
    "DCI001": "Unknown Error",
    "DCI002": "Buffer Index Out of Bounds (1)",
    "DCI003": "Buffer Index Out of Bounds (2)",
    "DCI004": "Buffer Index Out of Bounds (3)",
    "DCI005": "Buffer Index Out of Bounds (4)",
    "DCI006": "Profile Index Out of Bounds (1)",
    "DCI007": "Profile Index Out of Bounds (2)",
    "DCI008": "Controller Infusion Rate Too High",
    "DCI009": "User Infusion Rate Too High",
    "DCI010": "Same Object Used in Buffer Extract",
    "DCI011": "CIR/CF Array Size Incorrect",
    "DCI012": "Delivery Profile Array Size Incorrect",
    "DCI013": "Hash Check Failed (DanaReadWrite)",
    "DCI014": "Hash Check Failed (API)",
    // API exceptions - pump connectivity and configuration
    "DCI015": "Unknown Exception (Get Active Temp Basal)",
    "DCI016": "Unknown Exception (Connect Pump)",
    "DCI017": "Unknown Exception (Disconnect Pump)",
    "DCI018": "Next Pump (Unused)",
    "DCI019": "Unknown Exception (Get Pump Time)",
    "DCI020": "Unknown Exception (Sync Pump Time)",
    "DCI021": "Unknown Exception (Set CIR/CF Config)",
    "DCI022": "Unknown Exception (Get CIR/CF Config)",
    "DCI023": "Unknown Exception (Get Glucose Unit)",
    "DCI024": "Unknown Exception (Get Extended Bolus Allowed)",
    "DCI025": "Unknown Exception (Stop Temp Basal)",
    "DCI026": "Unknown Exception (Stop Extended Bolus)",
    "DCI027": "Unknown Exception (Get Suspend)",
    "DCI028": "Unknown Exception (Get Bolus Config)",
    "DCI029": "Unknown Exception (Set Bolus Config)",
    "DCI030": "Unknown Exception (Start Step Bolus)",
    "DCI031": "Unknown Exception (Start Temp Zero)",
    "DCI032": "Unknown Exception (Get Max Data)",
    "DCI033": "Unknown Exception (Get Active Insulin)",
    "DCI034": "Unknown Exception (Set Active Insulin)",
    "DCI035": "Unknown Exception (Get Preprogrammed Basal)",
    "DCI036": "Unknown Exception (Start Extended Bolus 30min)",
    "DCI037": "Unknown Exception (Set Carb Record)",
    "DCI038": "Unknown Exception (Set Time Marker)",
    // API exceptions - history downloads
    "DCI039": "Unknown Exception (Get Daily Total History)",
    "DCI040": "Unknown Exception (Get Bolus History)",
    "DCI041": "Unknown Exception (Get Carbohydrate History)",
    "DCI042": "Wrong Bolus Code",
    "DCI043": "Delivery Profile Pages Download (Unused)",
    "DCI044": "Unknown Exception (Get Delivered Basal)",
    "DCI045": "Unknown Exception (Get Pump Alarms)",
    "DCI046": "Unknown Exception (Get Pump Refill)",
    "DCI047": "Unknown Exception (Get Pump Prime)",
    "DCI048": "Carb History Download (Unused)",
    "DCI049": "TDD History Download (Unused)",
    "DCI050": "Bolus History Download (Unused)",
    // Connectivity / pairing / integrity
    "DCI051": "Cycle Bluetooth",
    "DCI052": "Pump Not Paired",
    "DCI053": "CRC Calculation Failed",
    "DCI054": "CRC Check Failed",
    "DCI055": "Factory Time to Pump Time Conversion Not Defined",
    // Database errors
    "DCI056": "Database Creation Failed",
    "DCI057": "Database Delete Failed",
    "DCI058": "Database Integrity Failed",
    "DCI059": "Database Context Null",
    "DCI060": "Database Open Exception",
    "DCI061": "Database Delete Exception (Old/Superfluous Data)",
    "DCI062": "Database Delete Exception",
    "DCI063": "Database Insert Failed",
    "DCI064": "Database Insert Exception",
    "DCI065": "Database Hash Check Failed",
    "DCI066": "Database Extract Exception",
    "DCI067": "Database Get Exception",
    "DCI068": "Inconsistent Parameters (Delivered Basal)",
    "DCI069": "Unknown Exception (Get Values From Pump)",
    "DCI070": "Null Element in Database Get",
    // Closed loop actions
    "DCI071": "Closed Loop Action (Not Used)",
    "DCI072": "Hash Check Failed (Closed Loop)",
    "DCI073": "Bolus Not Found (Closed Loop)",
    // Bolus delivery restrictions
    "DCI074": "Bolus Block Prevents Step Delivery",
    "DCI075": "Max Daily Delivery Exceeded (Step Bolus)",
    "DCI076": "Bolus Delivery Not Possible",
    "DCI077": "Max Daily Delivery Exceeded (Extended Bolus)",
    "DCI078": "Extended Bolus Not Allowed",
    // Closed loop operations / meal / exercise
    "DCI079": "Unknown Exception (Direct Closed Loop Rate)",
    "DCI080": "Unable to Stop Zero Temp Basal",
    "DCI081": "Unable to Stop Extended Bolus",
    "DCI082": "Exception (Start/Stop Closed Loop)",
    "DCI083": "Exception (Store Manual Meal Entry)",
    "DCI084": "Exception (Store Exercise Entry)",
    // Security / authentication
    "DCI085": "Pump Being Tampered By Another App",
    "DCI086": "Unknown Exception (Get Values Open Loop)",
    "DCI087": "Unknown Exception (Get Password)",
    "DCI088": "Unknown Exception (Get Day Delivered Insulin)",
    "DCI089": "Unknown Exception (Interrupt Closed Loop)",
    "DCI090": "Unused",
    "DCI091": "Unknown Exception (Stop Step Bolus)",
    "DCI092": "Encryption Settings Exception",
    "DCI093": "Pump Authentication Error",
    "DCI094": "New Passkey (Not Used)",
    "DCI095": "Unknown Exception (Get Decreasing Ratio)",
    "DCI096": "New Pairing Required",

    // ── DXC: Dexcom G5/G6 library ────────────────────────────────────────────
    // Source: dexcom HandleErrorMessage
    // Internal / connectivity errors
    "DXC001": "Unknown Error",
    "DXC002": "Cycle Bluetooth",
    "DXC003": "CRC Check Error",
    "DXC004": "CRC Calculation Error",
    "DXC005": "Wrong Transmitter Name",
    "DXC006": "Unknown Exception",
    // Transmitter status
    "DXC007": "Transmitter Already Connected to Another Phone",
    "DXC008": "Transmitter Low Battery",
    "DXC009": "Transmitter Failed",
    "DXC010": "Wakelock Error",
    "DXC011": "Transmitter - Last 22 Days",
    "DXC012": "Transmitter - Last 14 Days",
    "DXC013": "Last Transmitter Session Starting",
    "DXC014": "Last Transmitter Session Ending",
    // Sensor lifecycle
    "DXC015": "Sensor Expires In 6 Hours",
    "DXC016": "Sensor Expires In 2 Hours",
    "DXC017": "Sensor Expires In 30 Minutes",
    "DXC018": "Sensor Expired",
    // Glucose alerts
    "DXC019": "Urgent Low Glucose",
    "DXC020": "Low Glucose",
    "DXC021": "High Glucose",
    "DXC022": "Falling Glucose",
    "DXC023": "Rising Glucose",
    "DXC024": "Signal Loss",
    // Calibration (G5/G6 require finger-prick calibrations)
    "DXC025": "First Calibration Required",
    "DXC026": "Second Calibration Required",
    "DXC027": "Next Calibration Required",
    "DXC028": "Next New Calibration Required",
    "DXC029": "Planned Calibration",
    // Sensor / pairing status
    "DXC030": "Sensor Failed",
    "DXC031": "Pairing Failed",
    "DXC032": "Pairing In Progress",
    "DXC033": "Transmitter Paired",
    "DXC034": "Sensor Warm Up",
    // Session / calibration errors
    "DXC035": "Start Session Error",
    "DXC036": "End Session Error",
    "DXC037": "Calibration BG Error",
    "DXC038": "Invalid Calibration BG",
    // Database errors
    "DXC039": "Database Creation Failed",
    "DXC040": "Database Delete Failed",
    "DXC041": "Database Integrity Failed",
    "DXC042": "Database Context Null",
    "DXC043": "Database Open Exception",
    "DXC044": "Database Get Exception",
    "DXC045": "Database Get Exception",
    "DXC046": "Database Insert Failed",
    "DXC047": "Database Insert Exception",
    "DXC048": "Database Delete Exception",
    "DXC049": "Database Delete Exception",
    "DXC050": "Database Cipher Exception",
    // Misc / system
    "DXC051": "Transmitter End of Life",
    "DXC052": "GATT Error (Too Many)",
    "DXC053": "Message Acknowledgement Exception",
    "DXC054": "Restart App",
    "DXC055": "Sensor Restart Detected",
    "DXC056": "Urgent Low Glucose Soon",
    "DXC057": "Database Delete Exception",
    "DXC058": "No Glucose Readings",
    "DXC059": "Scanning Stuck",

    // ── LBR: Libre 3 library ──────────────────────────────────────────────────
    // Source: libre HandleErrorMessage (uk.ac.cam.ap.libre)
    // Internal / integrity errors
    "LBR001": "Unknown Error",
    "LBR002": "CRC Check Error",
    "LBR003": "CRC Calculation Error",
    "LBR004": "Gap Too Large Between Readings",
    "LBR005": "Wakelock Error",
    "LBR006": "Buffer Index Out of Bounds (1)",
    "LBR007": "Buffer Index Out of Bounds (2)",
    "LBR008": "Buffer Index Out of Bounds (3)",
    "LBR009": "Buffer Index Out of Bounds (4)",
    "LBR010": "Exception Saving Sensor Info",
    "LBR011": "Start Session Error",
    // Database errors
    "LBR012": "Database Creation Failed",
    "LBR013": "Database Delete Failed",
    "LBR014": "Database Integrity Failed",
    "LBR015": "Database Context Null",
    "LBR016": "Database Open Exception",
    "LBR017": "Database Get Exception",
    "LBR018": "Database Get Exception",
    "LBR019": "Database Insert Failed",
    "LBR020": "Database Insert Exception",
    "LBR021": "Database Delete Exception",
    "LBR022": "Database Delete Exception",
    "LBR023": "Handler Stuck (Transmitter End of Life)",
    "LBR024": "Message Acknowledgement Exception",
    "LBR025": "Database Delete Exception",
    // Sensor lifecycle (LBR050+ gap left for future critical errors)
    "LBR050": "Sensor Expires In 6 Hours",
    "LBR051": "Sensor Expires In 2 Hours",
    "LBR052": "Sensor Expires In 30 Minutes",
    "LBR053": "Sensor Expired",
    // Glucose alerts
    "LBR054": "Urgent Low Glucose",
    "LBR055": "Low Glucose",
    "LBR056": "High Glucose",
    "LBR057": "Falling Glucose",
    "LBR058": "Rising Glucose",
    "LBR059": "Signal Loss",
    // Sensor status / environmental
    "LBR060": "Sensor Failed",
    "LBR061": "Sensor Warm Up",
    "LBR062": "Urgent Low Glucose Soon",
    "LBR063": "No Glucose Readings",
    "LBR064": "Patch Temperature Too Low",
    "LBR065": "Patch Temperature Too High",
    "LBR066": "Scanning Already-Started Sensor",
    "LBR067": "Wrong BLE PIN",
    "LBR068": "Vitamin C Warning",   // High-dose vitamin C can interfere with Libre readings
    "LBR069": "Urgent High Ketones",
    "LBR070": "High Ketones",

    // ── YPS: Ypsomed 15x pump library ────────────────────────────────────────
    // Source: ypsomed_15x HandleErrorMessage (uk.ac.cam.ap.ypsomed_15x)
    // Internal data errors
    "YPS001": "Unknown Error",
    "YPS002": "Buffer Index Out of Bounds (1)",
    "YPS003": "Buffer Index Out of Bounds (2)",
    "YPS004": "Buffer Index Out of Bounds (3)",
    "YPS005": "Buffer Index Out of Bounds (4)",
    "YPS006": "Profile Index Out of Bounds (1)",
    "YPS007": "Profile Index Out of Bounds (2)",
    "YPS008": "Controller Infusion Rate Too High",
    "YPS009": "User Infusion Rate Too High",
    "YPS010": "Same Object Used in Buffer Extract",
    "YPS011": "CIR/CF Array Size Incorrect (Unused)",
    "YPS012": "Delivery Profile Array Size Incorrect",
    "YPS013": "Hash Check Failed (YpsoReadWrite)",
    "YPS014": "Hash Check Failed (API)",
    // API exceptions - pump connectivity and configuration
    "YPS015": "Unknown Exception (Get Active Temp Basal)",
    "YPS016": "Unknown Exception (Connect Pump)",
    "YPS017": "Unknown Exception (Disconnect Pump)",
    "YPS018": "Next Pump (Unused)",
    "YPS019": "Unknown Exception (Get Pump Time)",
    "YPS020": "Unknown Exception (Sync Pump Time)",
    "YPS021": "Set CIR/CF Config (Unused)",
    "YPS022": "Get CIR/CF Config (Unused)",
    "YPS023": "Get Glucose Unit (Unused)",
    "YPS024": "Get Extended Bolus Allowed (Not Used)",
    "YPS025": "Unknown Exception (Stop Temp Basal)",
    "YPS026": "Unknown Exception (Stop Extended Bolus)",
    "YPS027": "Unknown Exception (Get Suspend)",
    "YPS028": "Get Bolus Config (Unused)",
    "YPS029": "Set Pump Mode (Not Used)",
    "YPS030": "Unknown Exception (Start Step Bolus)",
    "YPS031": "Unknown Exception (Start Temp Zero)",
    "YPS032": "Unknown Exception (Get Max Data)",
    "YPS033": "Get Active Insulin (Unused)",
    "YPS034": "Set Active Insulin (Unused)",
    "YPS035": "Unknown Exception (Get Preprogrammed Basal)",
    "YPS036": "Unknown Exception (Start Extended Bolus 30min)",
    "YPS037": "Set Carb Record (Unused)",
    "YPS038": "Set Time Marker (Unused)",
    // API exceptions - history downloads
    "YPS039": "Unknown Exception (Get Daily Total History)",
    "YPS040": "Unknown Exception (Get Bolus History)",
    "YPS041": "Get Carb History (Not Used)",
    "YPS042": "Wrong Bolus Code (Not Used)",
    "YPS043": "Profile Pages Download (Unused)",
    "YPS044": "Unknown Exception (Get Delivered Basal)",
    "YPS045": "Unknown Exception (Get Pump Alarms)",
    "YPS046": "Unknown Exception (Get Pump Refill)",
    "YPS047": "Unknown Exception (Get Pump Prime)",
    "YPS048": "Carb History Download (Unused)",
    "YPS049": "TDD History Download (Unused)",
    "YPS050": "Bolus History Download (Unused)",
    // Connectivity / pairing / integrity
    "YPS051": "Cycle Bluetooth",
    "YPS052": "Pump Not Paired",
    "YPS053": "CRC Calculation Failed",
    "YPS054": "CRC Check Failed",
    "YPS055": "Factory Time to Pump Time Conversion Not Defined",
    // Database errors
    "YPS056": "Database Creation Failed",
    "YPS057": "Database Delete Failed",
    "YPS058": "Database Integrity Failed",
    "YPS059": "Database Context Null",
    "YPS060": "Database Open Exception",
    "YPS061": "Database Delete Exception (Old/Superfluous Data)",
    "YPS062": "Database Delete Exception",
    "YPS063": "Database Insert Failed",
    "YPS064": "Database Insert Exception",
    "YPS065": "Database Hash Check Failed",
    "YPS066": "Database Extract Exception",
    "YPS067": "Database Get Exception",
    "YPS068": "Inconsistent Parameters (Delivered Basal)",
    "YPS069": "Unknown Exception (Get Values From Pump)",
    "YPS070": "Null Element in Database Get",
    "YPS071": "Safe Integer Read Failed",
    // Closed loop actions
    "YPS072": "Hash Check Failed (Closed Loop)",
    "YPS073": "Bolus Not Found (Closed Loop)",
    // Bolus delivery restrictions
    "YPS074": "Bolus Block Prevents Step Delivery",
    "YPS075": "Max Daily Delivery Exceeded (Step Bolus)",
    "YPS076": "Bolus Delivery Not Possible",
    "YPS077": "Max Daily Delivery Exceeded (Extended Bolus)",
    "YPS078": "Extended Bolus Not Allowed",
    // Closed loop operations / meal / exercise
    "YPS079": "Unknown Exception (Direct Closed Loop Rate)",
    "YPS080": "Unable to Stop Zero Temp Basal",
    "YPS081": "Unable to Stop Extended Bolus",
    "YPS082": "Exception (Start/Stop Closed Loop)",
    "YPS083": "Exception (Store Manual Meal Entry)",
    "YPS084": "Exception (Store Exercise Entry)",
    // Security / authentication
    "YPS085": "Pump Being Tampered By Another App",
    "YPS086": "Unknown Exception (Get Values Open Loop)",
    "YPS087": "Get Password (Not Used)",
    "YPS088": "Get Day Delivered Insulin (Not Used)",
    "YPS089": "Unknown Exception (Interrupt Closed Loop)",
    "YPS090": "Unknown Exception (Execute Pump Events Download)",
    "YPS091": "Unknown Exception (Stop Step Bolus)",
    "YPS092": "Encryption Settings Exception (Not Used)",
    "YPS093": "Pump Error (Not Used)",
    "YPS094": "New Passkey (Not Used)",
    "YPS095": "Get Decreasing Ratio (Not Used)",
    "YPS096": "New Pairing Required",
    "YPS097": "Key Exchange Failed",
    "YPS098": "Pump Not Compatible",
    "YPS099": "Unknown Exception (Get Country Code)",

    // ── FLW: Cloud Follow CGM library ────────────────────────────────────────
    // Source: follow_cgm HandleErrorMessage (uk.ac.cam.ap.follow_cgm)
    // Used when CamAPS FX acts as a CGM follower via the cloud
    // Internal / integrity errors
    "FLW001": "Unknown Error",
    "FLW002": "CRC Check Error",
    "FLW003": "CRC Calculation Error",
    "FLW004": "Unknown Exception",
    "FLW005": "Wakelock Error",
    "FLW006": "Buffer Index Out of Bounds (1)",
    "FLW007": "Buffer Index Out of Bounds (2)",
    "FLW008": "Buffer Index Out of Bounds (3)",
    "FLW009": "Buffer Index Out of Bounds (4)",
    "FLW010": "Exception Setting EGV",      // Error storing estimated glucose value
    "FLW011": "Start Session Error (Unused)",
    // Database errors
    "FLW012": "Database Creation Failed",
    "FLW013": "Database Delete Failed",
    "FLW014": "Database Integrity Failed",
    "FLW015": "Database Context Null",
    "FLW016": "Database Open Exception",
    "FLW017": "Database Get Exception",
    "FLW018": "Database Get Exception",
    "FLW019": "Database Insert Failed",
    "FLW020": "Database Insert Exception",
    "FLW021": "Database Delete Exception",
    "FLW022": "Database Delete Exception",
    "FLW023": "Handler Stuck (Transmitter End of Life)",
    "FLW024": "Message Acknowledgement Exception",
    "FLW025": "Database Delete Exception",
    // Sensor lifecycle (FLW050+ gap left for future critical errors)
    "FLW050": "Sensor Expires In 6 Hours",
    "FLW051": "Sensor Expires In 2 Hours",
    "FLW052": "Sensor Expires In 30 Minutes",
    "FLW053": "Sensor Expired",
    // Glucose alerts
    "FLW054": "Urgent Low Glucose",
    "FLW055": "Low Glucose",
    "FLW056": "High Glucose",
    "FLW057": "Falling Glucose",
    "FLW058": "Rising Glucose",
    "FLW059": "Signal Loss",
    // Sensor status
    "FLW060": "Sensor Warm Up",
    "FLW061": "Urgent Low Glucose Soon",
    "FLW062": "No Glucose Readings",
};

/**
 * Decodes a message ID string into a human-readable label using {@link MESSAGE_ID_MAP}.
 * If the ID is found, returns `"ID (Description)"`. If not found, returns the raw ID unchanged.
 * If `msgID` is falsy, returns an empty string.
 * @param {string} msgID - The message ID to decode (e.g. `"FXM074"`, `"FXMCGM001"`).
 * @returns {string} Decoded label, or the original ID if unknown, or `""` if falsy.
 */
function decodeMessageID(msgID) {
    if (!msgID) return "";
    const decoded = MESSAGE_ID_MAP[msgID];
    return decoded ? `${msgID} (${decoded})` : msgID;
}

/**
 * Writes a timestamped alert/notification record to the output, including a decoded message ID.
 * Output format: timestamp + space + decoded message ID (e.g. `"FXM074 (Low Reservoir)"`)
 * Used for audio, vibrate, displayed, and acknowledged alert categories.
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and optional `msgID` string.
 */
function writeDateStampAndMessageID(self, obj) {
    let message = "";
    if (obj.msgID) {
        message = decodeMessageID(obj.msgID);
    }
    addOutputLine(self, getDateTime(obj.date) + " " + message)
}

/**
 * Writes a pump alarm record to the output.
 * Output format: timestamp \t\t alarm_code
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and `alarm_code.val` (numeric pump alarm code).
 */
function writePumpAlarm(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + obj.alarm_code.val)
}

/**
 * Writes a total daily dose (TDD) record to the output.
 * Output format: timestamp \t\t total(U/day) \t extBolus(U/day) \t basal(U/day)
 * Basal is derived as TDD minus extended bolus.
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date`, `tdd.val` (U/day), and `extBolus.val` (U/day).
 */
function writeTdd(self, obj) {
    addOutputLine(self, getDateTime(obj.date)
        + '\t\t' + obj.tdd.val
        + '\t' + obj.extBolus.val
        + '\t' + (obj.tdd.val - obj.extBolus.val));
}

/**
 * Writes an algorithm-calculated TDD record to the output.
 * Uses `tdd.val` if present, otherwise falls back to `tdd.amSt` or finally `weight.val`.
 * Output format: timestamp \t\t calculated_value
 * @param {object} self - Output state object passed to {@link addOutputLine}.
 * @param {object} obj - Data record containing `date` and either `tdd` (with `val` or `amSt`) or `weight.val`.
 */
function writeTddAlgo(self, obj) {
    addOutputLine(self, getDateTime(obj.date) + '\t\t' + (
        obj.tdd ? (obj.tdd.val ? obj.tdd.val : obj.tdd.amSt): obj.weight.val));
}

/**
 * Reads and outputs aggressiveness (closed-loop sensitivity level) records for a user.
 * Columns: Time | level (%) | duration (minutes)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readAggressiveness(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding aggressiveness ");
    addOutputLine(self, "");
    addOutputLine(self, "Aggressiveness ********************************************");
    addOutputLine(self, "Time                        \tlevel \t duration");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)          \t(%) \t (minutes)");

    await processDataCategory(userid, version, startTime, endTime, self, modeAggressiveness, writeAggressiveness);
}

/**
 * Reads and outputs closed-loop attempting-mode status records for a user.
 * Columns: Time | mode string
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @param {boolean} perform - If false, writes the section header only without fetching data.
 * @returns {Promise<void>}
 */
export async function readClAttempting(self, userid, startTime, endTime, version, perform) {
    addContentNewLine(self, "");
    addContent(self, "Adding attempting mode");
    addOutputLine(self, "");
    addOutputLine(self, "Attempting_mode ********************************************");
    addOutputLine(self, "Time                        \tmode");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    if (perform) {
        await processDataCategory(userid, version, startTime, endTime, self, modeAttempting, writeAttempting);
    }
}

/**
 * Reads and outputs auto-mode statistics records for a user, followed by a percentage summary.
 * Per-record columns: Time | CL_mode | FCL_mode
 * Summary columns: Automode_on (%) | Automode_off (%) | Automode_attempting (%)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<string>} Two-character zero-padded auto-mode-on percentage followed by `"_"`,
 *   used as part of a report filename or summary key.
 */
export async function readAutoModeStats(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding automode for stats");
    addOutputLine(self, "");
    addOutputLine(self, "Auto_mode_for_stats  ********************************************");
    addOutputLine(self, "Time                        \tCL_mode \t\tFCL_mode ");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    const items = await processDataCategoryStats(userid, version, startTime, endTime, self, modeStats, writeAutoModeStats);

    addContentNewLine(self, "");
    addContent(self, "Adding summary automode for stats");
    addOutputLine(self, "");
    addOutputLine(self, "Summary_auto_mode_for_stats  ********************************************");
    addOutputLine(self, "Automode_on \tAutomode_off \tAutomode_attempting");
    addOutputLine(self, "(%)                   \t(%)                   \t(%)");
    addOutputLine(self,
        Math.round(items[0]*100) + "            \t"
        + Math.round(items[1]*100) + "                \t"
        + Math.round(items[2]*100));

    return Math.round(items[0]*100).toString().padStart(2) + "_";
}

/**
 * Reads and outputs personal glucose target profile change records for a user.
 * Only records where the profile differs from the previous record are written.
 * Columns: Time | target glucose values (mmol/L)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @returns {Promise<void>}
 */
export async function readTargetGlucose(self, userid, startTime, endTime) {
    addContentNewLine(self, "");
    addContent(self, "Adding target glucose");
    addOutputLine(self, "");
    addOutputLine(self, "Target_glucose ********************************************");
    addOutputLine(self, "Time                        \tTarget glucose ");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)          \t(mmol/L) ");

    await processSettings(userid, startTime, endTime, self, writeTargetGlucose, checkIfDifferentFromPreviousGlucoseTarget);
}

/**
 * Reads and outputs preprogrammed basal insulin profile change records for a user.
 * Only records where the profile differs from the previous record are written.
 * Columns: Time | basal profile values (U/h)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @returns {Promise<void>}
 */
export async function readBasalInsulin(self, userid, startTime, endTime) {
    addContentNewLine(self, "");
    addContent(self, "Adding basal insulin profile");
    addOutputLine(self, "");
    addOutputLine(self, "Basal_insulin_profile ********************************************");
    addOutputLine(self, "Time                        \tBasal insulin profile ");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)          \t(U/h)");

    await processSettings(userid, startTime, endTime, self, writeBasalProfile, checkIfDifferentFromPreviousBasalProfile);
}

/**
 * Reads and outputs correction factor (CF) profile change records for a user.
 * Only records where the profile differs from the previous record are written.
 * Columns: Time | CF profile values + units
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @returns {Promise<void>}
 */
export async function readCfProfile(self, userid, startTime, endTime) {
    addContentNewLine(self, "");
    addContent(self, "Adding CF profile");
    addOutputLine(self, "");
    addOutputLine(self, "CF_profile ********************************************");
    addOutputLine(self, "Time                        \tCF profile ");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)          \t");

    await processSettings(userid, startTime, endTime, self, writeCfProfile, checkIfDifferentFromPreviousCfProfile);
}

/**
 * Reads and outputs insulin-to-carb ratio (ICR) profile change records for a user.
 * Only records where the profile differs from the previous record are written.
 * Columns: Time | ICR profile values + units
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @returns {Promise<void>}
 */
export async function readIcrProfile(self, userid, startTime, endTime) {
    addContentNewLine(self, "");
    addContent(self, "Adding ICR profile");
    addOutputLine(self, "");
    addOutputLine(self, "ICR_profile ********************************************");
    addOutputLine(self, "Time                        \tICR profile ");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)          \t");

    await processSettings(userid, startTime, endTime, self, writeIcrProfile, checkIfDifferentFromPreviousIcrProfile);
}

/**
 * Reads and outputs body weight records for a user.
 * Columns: Time | weight (kg)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readWeight(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding weight");
    addOutputLine(self, "");
    addOutputLine(self, "Weight  ********************************************");
    addOutputLine(self, "Time                        \tweight");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           (kg)");

    await processDataCategory(userid, version, startTime, endTime, self, modeWeight, writeBodyWeight);
}

/**
 * Reads and outputs closed-loop (auto) mode status records for a user.
 * Columns: Time | mode string (off / on / trying / stopping / starting)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @param {boolean} perform - If false, writes the section header only without fetching data.
 * @returns {Promise<void>}
 */
export async function readAutoMode(self, userid, startTime, endTime, version, perform) {
    addContentNewLine(self, "");
    addContent(self, "Adding auto mode");
    addOutputLine(self, "");
    addOutputLine(self, "Auto_mode ********************************************");
    addOutputLine(self, "Time                        \tmode");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    if (perform) {
        await processDataCategory(userid, version, startTime, endTime, self, modeCl_mode, writeClMode);
    }
}

/**
 * Reads and outputs insulin reservoir level records for a user.
 * Columns: Time | amount (U)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readReservoir(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding reservoir");
    addOutputLine(self, "");
    addOutputLine(self, "Reservoir_remaining  ********************************************");
    addOutputLine(self, "Time                        \tamount");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           (U)");

    await processDataCategory(userid, version, startTime, endTime, self, modeReservoir, writeReservoir);
}

/**
 * Reads and outputs pump priming event records (timestamps only) for a user.
 * Columns: Time
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readPriming(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding priming");
    addOutputLine(self, "");
    addOutputLine(self, "Priming_event ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modePriming, writePriming);
}

/**
 * Reads and outputs insulin reservoir refill event records (timestamps only) for a user.
 * Columns: Time
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readRefill(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding refill");
    addOutputLine(self, "");
    addOutputLine(self, "Refill_event ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeRefill, writeDateStamp);
}


/**
 * Reads and outputs algorithmically deduced refill event records (timestamps only) for a user.
 * These are refill events inferred from data rather than directly recorded by the pump.
 * Columns: Time
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readRefillDeduced(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding refill deduced");
    addOutputLine(self, "");
    addOutputLine(self, "Refill_event_deduced ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeRefillDeduced, writeDateStamp);
}

/**
 * Reads and outputs pump alarm records for a user.
 * Columns: Time | alarm code (numeric)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readPumpAlarm(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding pump alarms");
    addOutputLine(self, "");
    addOutputLine(self, "Pump alarm  ********************************************");
    addOutputLine(self, "Time                        \tcode");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modePump_alarm, writePumpAlarm);
}


/**
 * Reads and outputs CGM sensor insertion / warm-up start event records (timestamps only) for a user.
 * Columns: Time
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readSensorStarted(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding sensor inserted");
    addOutputLine(self, "");
    addOutputLine(self, "Sensor_inserted ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeWarm_up, writeDateStamp);
}


/**
 * Reads and outputs CGM sensor stopped event records (timestamps only) for a user.
 * Columns: Time
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readSensorStopped(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding sensor stopped");
    addOutputLine(self, "");
    addOutputLine(self, "Sensor_stopped ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeSensor_stopped, writeDateStamp);
}

/**
 * Reads and outputs audio alert event records for a user, with decoded message IDs.
 * Columns: Time | message ID (decoded)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readAudioAlert(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding audio alerts");
    addOutputLine(self, "");
    addOutputLine(self, "Audio_alerts ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeAlert_audio, writeDateStampAndMessageID);
}

/**
 * Reads and outputs vibrate alert event records for a user, with decoded message IDs.
 * Columns: Time | message ID (decoded)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readVibrateAlert(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding vibrate alerts");
    addOutputLine(self, "");
    addOutputLine(self, "Vibrate_alerts ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeAlert_vibrate, writeDateStampAndMessageID);
}

/**
 * Reads and outputs alert-displayed event records for a user, with decoded message IDs.
 * Columns: Time | message ID (decoded)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readDisplayedAlert(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding alerts displayed");
    addOutputLine(self, "");
    addOutputLine(self, "Displayed_alerts ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeAlert_displayed,
        writeDateStampAndMessageID);
}

/**
 * Reads and outputs alert-acknowledged event records for a user, with decoded message IDs.
 * Columns: Time | message ID (decoded)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readAcknowledgedAlert(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding alerts acknowledged");
    addOutputLine(self, "");
    addOutputLine(self, "Acknowledged_alerts ********************************************");
    addOutputLine(self, "Time                        \t");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    await processDataCategory(userid, version, startTime, endTime, self, modeAlert_acknowledged,
        writeDateStampAndMessageID);
}

/** Label strings used to identify each alert category in the combined output. */
const ALERT_TYPE_SOUNDED      = "sounded";
const ALERT_TYPE_VIBRATED     = "vibrated";
const ALERT_TYPE_DISPLAYED    = "displayed";
const ALERT_TYPE_ACKNOWLEDGED = "acknowledged";

/**
 * Reads all four alert categories (audio, vibrate, displayed, acknowledged) in parallel,
 * merges them into a single list sorted chronologically, and writes each entry with a type label.
 *
 * This provides a unified view of all alert activity for a user, making it easy to trace
 * the full lifecycle of an alert (displayed → sounded/vibrated → acknowledged).
 *
 * Output format per row: timestamp \t type \t decoded message ID
 *
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readAllAlerts(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding all alerts combined");
    addOutputLine(self, "");
    addOutputLine(self, "All_alerts ********************************************");
    addOutputLine(self, "Time                        \ttype          \tmessage");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           ");

    // Fetch all four categories in parallel for efficiency
    const [audioItems, vibrateItems, displayedItems, acknowledgedItems] = await Promise.all([
        collectDataCategory(userid, version, startTime, endTime, self, modeAlert_audio),
        collectDataCategory(userid, version, startTime, endTime, self, modeAlert_vibrate),
        collectDataCategory(userid, version, startTime, endTime, self, modeAlert_displayed),
        collectDataCategory(userid, version, startTime, endTime, self, modeAlert_acknowledged),
    ]);

    // Tag each item with its alert type before merging
    const taggedItems = [
        ...audioItems.map(obj        => ({ ...obj, _alertType: ALERT_TYPE_SOUNDED })),
        ...vibrateItems.map(obj      => ({ ...obj, _alertType: ALERT_TYPE_VIBRATED })),
        ...displayedItems.map(obj    => ({ ...obj, _alertType: ALERT_TYPE_DISPLAYED })),
        ...acknowledgedItems.map(obj => ({ ...obj, _alertType: ALERT_TYPE_ACKNOWLEDGED })),
    ];

    // Sort chronologically — date strings are ISO-format so lexicographic order is correct
    taggedItems.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));

    // Write each item
    for (const item of taggedItems) {
        if (!isContinueProcessing(self)) {
            return;
        }
        const message = item.msgID ? decodeMessageID(item.msgID) : "";
        addOutputLine(self, getDateTime(item.date) + '\t' + item._alertType.padEnd(14) + '\t' + message);
    }
}

/**
 * Reads and outputs total daily dose (TDD) records for a user.
 * Columns: Time | total (U/day) | extended bolus (U/day) | basal (U/day)
 * Basal is derived as total minus extended bolus.
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readTdd(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding TDD");
    addOutputLine(self, "");
    addOutputLine(self, "TDD_value ********************************************");
    addOutputLine(self, "Time                        \ttotal \tbasal \tbolus");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           (U/day) (U/day) (U/day)");

    await processDataCategory(userid, version, startTime, endTime, self, modeTdd, writeTdd);
}

/**
 * Reads and outputs algorithm-calculated TDD records for a user.
 * The value written is `tdd.val` if available, otherwise `tdd.amSt`, otherwise `weight.val`.
 * Columns: Time | calculated value (U/day)
 * @param {object} self - Output state object.
 * @param {string} userid - The user ID to query.
 * @param {number} startTime - Query start time (epoch ms).
 * @param {number} endTime - Query end time (epoch ms).
 * @param {string} version - Data schema version string.
 * @returns {Promise<void>}
 */
export async function readTddAlgo(self, userid, startTime, endTime, version) {
    addContentNewLine(self, "");
    addContent(self, "Adding TDD algorithm");
    addOutputLine(self, "");
    addOutputLine(self, "Algorithm_TDD_value ********************************************");
    addOutputLine(self, "Time                        \tcalculated");
    addOutputLine(self, "(dd/mm/yyyy hh:mm)           (U/day)");

    await processDataCategory(userid, version, startTime, endTime, self, modeTdd_algorithm, writeTddAlgo);
}

/**
 * Converts a numeric closed-loop attempting-mode status code to a human-readable string.
 * Status codes mirror the `cl_mode` values stored by the CamAPS FX app.
 * Returns `"unknown"` for any unrecognised code.
 * @param {number} attemptingStatus - Numeric status code from the data record's `cl_mode` field.
 * @returns {string} Human-readable mode label, e.g. `"starting"`, `"no_cgm"`, `"depowered"`.
 */
function getAttemptingString(attemptingStatus) {
    let result = "unknown";
    switch (attemptingStatus) {
        case 1:
            result = "starting";
            break;
        case 2:
            result = "stopping";
            break;
        case 3:
            result = "no_cgm";
            break;
        case 4:
            result = "no_pump_connectivity";
            break;
        case 5:
            result = "pump_delivery_suspended";
            break;
        case 6:
            result = "extended_bolus_not_allowed";
            break;
        case 7:
            result = "daily_total_insulin_exceeded";
            break;
        case 8:
            result = "bluetooth_turned_off";
            break;
        case 9:
            result = "unable_to_start_closed_loop_cycle";
            break;
        case 10:
        case 11:
            result = "depowered";
            break;
        case 12:
            result = "auto_mode actually off";
            break;
        case 13:
            result = "auto mode actually on";
            break;
        default:
    }
    return result;
}

/**
 * Converts a numeric closed-loop (auto) mode code to a human-readable string.
 * Mode codes: 0 = off, 1 = on, 2 = trying, 3 = stopping, 4 = starting.
 * Returns an empty string for any unrecognised code.
 * @param {number} mode - Numeric mode code from the data record's `cl_mode` field.
 * @returns {string} Human-readable mode label, e.g. `"on"`, `"trying"`, or `""` if unknown.
 */
function getAutoModeString(mode) {
    let value = "";
    switch (mode) {
        case 0:
            value = "off";
            break;
        case 1:
            value = "on";
            break;
        case 2:
            value = "trying";
            break;
        case 3:
            value = "stopping";
            break;
        case 4:
            value = "starting";
            break;
        default:
            break;
    }
    return value;
}